/**
 * PURE table-model builder for the unified Table & Matrix family.
 *
 * No Angular dependency — takes capabilities + flat rows and returns a plain
 * `TableModel` (columns + rows + conditional-format + subtotals/grand-total), so
 * all shaping logic is unit-testable. The component just renders the model as an
 * HTML/mat-table. This is the table analog of `buildBarColumnOption`.
 *
 * `tableType` (via `typeFlags`) selects behaviour (no name-string checks):
 *   normal    → pick columns, optional group-by subtotals + grand total.
 *   editable  → same column model + per-column control/validation (edit UI is the
 *               component's job; this builder just shapes columns/rows).
 *   drilldown → handled by the existing lib-drill-down path, NOT this builder
 *               (the component renders drill-down via its own widget data).
 *
 * EVERY styling/format constant is read from `capabilities.chartDefaults` (from
 * JSON `tableMatrixChartDefaults`). FALLBACK is a last-resort safety net ONLY.
 */
import type {
  TableMatrixCapabilities,
  TableMatrixColumn,
  TableMatrixConditionalRule,
  TableModel,
  TableModelCell,
  TableModelColumn,
  TableModelColumnGroup,
  TableModelRow,
} from './unified-table-matrix-chart.types';

type Row = Record<string, unknown>;

/** LAST-RESORT safety net ONLY — authoritative values live in the JSON
 *  (`tableMatrixChartDefaults`). Used when both the per-tile capability AND the
 *  config are absent, so a broken/missing config never throws. */
const FALLBACK = {
  defaultColumnWidth: 140,
  totalGroupLabel: 'Total',
  grandTotalLabel: 'Grand Total',
};

/** Numeric coercion tolerant of "1,234"/"$1.2k" → numeric part; non-numeric → null. */
function toNum(value: unknown): number | null {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value === 'string') {
    const n = Number(value.replace(/[^0-9.eE+-]/g, ''));
    return Number.isFinite(n) && value.trim() !== '' ? n : null;
  }
  return null;
}

/** Aggregate a numeric column of rows by the given mode. */
function aggregate(values: number[], mode: string): number {
  if (!values.length) return 0;
  switch (mode) {
    case 'avg': return values.reduce((a, b) => a + b, 0) / values.length;
    case 'count': return values.length;
    case 'distinct': return new Set(values).size;
    case 'min': return Math.min(...values);
    case 'max': return Math.max(...values);
    case 'sum':
    default: return values.reduce((a, b) => a + b, 0);
  }
}

/** The active aggregation mode (defaults to 'sum' when unset / 'none'). */
function aggMode(capabilities: TableMatrixCapabilities): string {
  const a = capabilities.aggregation;
  return a && a !== 'none' ? a : 'sum';
}

/** Sum/aggregate one numeric field across a set of rows. */
function aggField(rows: Row[], field: string, mode: string): number {
  const vals = rows.map((r) => toNum(r[field])).filter((n): n is number => n !== null);
  return aggregate(vals, mode);
}

/** Lightweight number formatter (kept local so the builder has no Angular dep). */
function formatValue(value: number, capabilities: TableMatrixCapabilities): string {
  if (!Number.isFinite(value)) return '-';
  const scale = capabilities.numberScale || 'none';
  const fmt = capabilities.numberFormat || 'NONE';
  const decimals = Math.max(0, Math.min(6, Math.floor(capabilities.decimals ?? 1)));
  let scaled = value;
  let suffix = '';
  switch (scale) {
    case 'thousands': case 'SCALE_THOUSANDS': scaled = value / 1e3; suffix = 'K'; break;
    case 'millions': case 'MILLIONS': scaled = value / 1e6; suffix = 'M'; break;
    case 'billions': case 'BILLIONS': scaled = value / 1e9; suffix = 'B'; break;
    case 'compact': case 'COMPACT': {
      const abs = Math.abs(value);
      if (abs >= 1e9) { scaled = value / 1e9; suffix = 'B'; }
      else if (abs >= 1e6) { scaled = value / 1e6; suffix = 'M'; }
      else if (abs >= 1e3) { scaled = value / 1e3; suffix = 'K'; }
      break;
    }
    default: break;
  }
  if (fmt === 'PERCENT' || fmt === 'percent') return `${(value * 100).toFixed(decimals)}%`;
  const useCommas = fmt === 'THOUSANDS' || fmt === 'thousands' || suffix === '';
  const body = useCommas && !suffix
    ? scaled.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: decimals })
    : scaled.toFixed(decimals);
  return `${body}${suffix}`;
}

/** Format one cell's display text by column type. */
function displayFor(value: unknown, col: TableModelColumn, capabilities: TableMatrixCapabilities): string {
  if (value === undefined || value === null || value === '') return '';
  if (col.type === 'number' || col.type === 'currency' || col.type === 'percent') {
    const n = toNum(value);
    if (n === null) return String(value);
    const body = formatValue(n, capabilities);
    if (col.type === 'currency') return `$${body}`;
    if (col.type === 'percent' && !(capabilities.numberFormat || '').toUpperCase().includes('PERCENT')) return `${body}%`;
    return body;
  }
  return String(value);
}

/** Whether a value satisfies a conditional-format rule. */
function ruleMatches(value: unknown, rule: TableMatrixConditionalRule): boolean {
  const n = toNum(value);
  if (n === null) return false;
  switch (rule.operator) {
    case '>': return n > rule.value;
    case '>=': return n >= rule.value;
    case '<': return n < rule.value;
    case '<=': return n <= rule.value;
    case '=': return n === rule.value;
    case '!=': return n !== rule.value;
    case 'between': return rule.value2 !== undefined && n >= rule.value && n <= rule.value2;
    default: return false;
  }
}

/** Resolve the conditional-format styles for a cell (first matching rule wins). */
function conditionalStyle(field: string, value: unknown, rules: TableMatrixConditionalRule[]): { background?: string; color?: string } {
  for (const rule of rules) {
    if (rule.field === field && ruleMatches(value, rule)) {
      return { background: rule.background, color: rule.color };
    }
  }
  return {};
}

/** Normalize an editable column's inline dropdown options (bare strings OR
 *  {value,label}) into a uniform {value,label} list. */
function resolveColumnOptions(
  opts?: Array<{ value: string; label?: string } | string>,
): Array<{ value: string; label: string }> | undefined {
  if (!Array.isArray(opts) || !opts.length) return undefined;
  return opts.map((o) =>
    typeof o === 'string'
      ? { value: o, label: o }
      : { value: String(o.value), label: String(o.label ?? o.value) },
  );
}

/** A stable per-row id for change-tracking (uses an existing id-like field when
 *  present, else the row's ordinal — deterministic so re-renders keep the key). */
function rowIdFor(raw: Row, index: number): string {
  const cand = raw['id'] ?? raw['rowId'] ?? raw['_id'] ?? raw['uuid'];
  return cand !== undefined && cand !== null && cand !== '' ? String(cand) : `r${index}`;
}

/** Distinct values of a column in first-seen order. */
function distinct(rows: Row[], field: string): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  rows.forEach((r) => {
    const k = String(r[field] ?? '');
    if (!seen.has(k)) { seen.add(k); out.push(k); }
  });
  return out;
}

/**
 * Build the client-side TableModel from flat rows + capabilities.
 * Returns `{ columns:[], rows:[], empty:true }` when config is incomplete.
 */
export function buildTableModel(rows: Array<Record<string, unknown>>, capabilities: TableMatrixCapabilities): TableModel {
  const safeRows = Array.isArray(rows) ? rows : [];

  // Dispatch to the shape-specific builder by type flag (falls through to the
  // flat Normal/Editable model below). typeFlags is config-resolved; name check
  // is a fallback when flags are absent.
  const f = capabilities.typeFlags || {};
  const t = capabilities.tableType;
  if (f['matrix'] || t === 'matrix') return buildMatrixModel(safeRows, capabilities);
  if (f['pivot'] || t === 'pivot') return buildPivotModel(safeRows, capabilities);
  if (f['grouped'] || t === 'grouped') return buildGroupedModel(safeRows, capabilities);
  if (f['treegrid'] || t === 'treegrid') return buildTreeGridModel(safeRows, capabilities);
  if (f['financial'] || t === 'financial') return buildFinancialModel(safeRows, capabilities);

  const d = capabilities.chartDefaults || {};
  const defaultWidth = (d.defaultColumnWidth as number) ?? FALLBACK.defaultColumnWidth;

  // Column model: use configured columns, else infer from the first row's keys.
  // If NONE of the configured fields exist on the actual rows, the config is
  // stale (e.g. built against a different response shape) — fall back to the
  // row keys rather than rendering a table full of blank cells.
  const configured = Array.isArray(capabilities.columns) ? capabilities.columns : [];
  const rowKeys = new Set(safeRows[0] ? Object.keys(safeRows[0]) : []);
  const configuredMatchesData = configured.some((c) => rowKeys.has(c.field));
  const columnDefs: TableMatrixColumn[] = configured.length && configuredMatchesData
    ? configured
    : (safeRows[0] ? Object.keys(safeRows[0]).map((field) => ({ field })) : []);

  if (!columnDefs.length) return { columns: [], rows: [], empty: true };

  // Editable tables need a control per column to render an <input> cell. When
  // the configured columns didn't match the data (fallback above) that config
  // — including its `control` — was dropped, so default to a plain text
  // control here rather than silently losing the editable UI.
  const isEditableTable = capabilities.typeFlags?.['editable'] === true || capabilities.tableType === 'editable';

  const columns: TableModelColumn[] = columnDefs.map((c) => {
    const type = c.type || (safeRows.length && toNum(safeRows[0][c.field]) !== null ? 'number' : 'text');
    return {
      key: c.field,
      label: c.label || c.field,
      type,
      align: c.align || (type === 'number' || type === 'currency' || type === 'percent' ? 'end' : 'start'),
      width: c.width || defaultWidth,
      frozen: c.frozen === true || (capabilities.freezeFirstColumn === true && c.field === columnDefs[0].field),
      control: c.control || (isEditableTable ? 'text' : undefined),
      optionsKey: c.optionsKey,
      options: resolveColumnOptions(c.options),
      lookupUrl: c.lookupUrl,
      hrefTemplate: c.hrefTemplate,
      buttonLabel: c.buttonLabel,
      buttonAction: c.buttonAction,
      required: c.required,
      min: c.min,
      max: c.max,
      regex: c.regex,
      unique: c.unique,
      requiredWhen: c.requiredWhen,
      gteField: c.gteField,
      lteField: c.lteField,
    };
  });

  const rules = Array.isArray(capabilities.conditionalRules) ? capabilities.conditionalRules : [];
  const numericCols = columns.filter((c) => c.type === 'number' || c.type === 'currency' || c.type === 'percent');

  // Build one rendered row (with per-cell display + conditional styles). Data
  // rows carry a stable rowId (editable change-tracking); the raw row's own
  // `__state`/`__rowId` (set by client-side add/edit) is surfaced onto the model.
  const makeRow = (raw: Row, kind: TableModelRow['kind'], groupLabel?: string, index = 0): TableModelRow => {
    const cells: Record<string, TableModelCell> = {};
    columns.forEach((col) => {
      const value = raw[col.key];
      const style = kind === 'data' ? conditionalStyle(col.key, value, rules) : {};
      cells[col.key] = { value, display: displayFor(value, col, capabilities), background: style.background, color: style.color };
    });
    const base: TableModelRow = { cells, raw: kind === 'data' ? raw : undefined, kind, groupLabel };
    if (kind === 'data') {
      base.rowId = (raw['__rowId'] as string) || rowIdFor(raw, index);
      const st = raw['__state'] as TableModelRow['state'] | undefined;
      if (st) base.state = st;
    }
    return base;
  };

  const modelRows: TableModelRow[] = [];
  const groupBy = capabilities.groupBy && columns.some((c) => c.key === capabilities.groupBy) ? capabilities.groupBy : '';
  const wantSubtotals = capabilities.showSubtotals === true && !!groupBy;

  if (groupBy) {
    // Group rows; optional subtotal per group.
    distinct(safeRows, groupBy).forEach((g) => {
      const groupRows = safeRows.filter((r) => String(r[groupBy] ?? '') === g);
      groupRows.forEach((r, gi) => modelRows.push(makeRow(r, 'data', undefined, gi)));
      if (wantSubtotals) {
        const subtotal: Row = { [columns[0].key]: `${g} ${FALLBACK.totalGroupLabel}` };
        numericCols.forEach((col) => {
          const vals = groupRows.map((r) => toNum(r[col.key])).filter((n): n is number => n !== null);
          subtotal[col.key] = aggregate(vals, capabilities.aggregation && capabilities.aggregation !== 'none' ? capabilities.aggregation : 'sum');
        });
        modelRows.push({ ...makeRow(subtotal, 'subtotal', g), groupLabel: g });
      }
    });
  } else {
    safeRows.forEach((r, i) => modelRows.push(makeRow(r, 'data', undefined, i)));
  }

  // Grand total row.
  if (capabilities.showGrandTotal === true && numericCols.length) {
    const grand: Row = { [columns[0].key]: FALLBACK.grandTotalLabel };
    numericCols.forEach((col) => {
      const vals = safeRows.map((r) => toNum(r[col.key])).filter((n): n is number => n !== null);
      grand[col.key] = aggregate(vals, capabilities.aggregation && capabilities.aggregation !== 'none' ? capabilities.aggregation : 'sum');
    });
    modelRows.push(makeRow(grand, 'grandtotal'));
  }

  // "Empty" means the table has NO COLUMNS to render (config incomplete). With
  // columns but zero rows we still render the header + a "no data" body, so the
  // user sees their configured columns instead of the misleading "Map columns".
  return { columns, rows: modelRows, empty: columns.length === 0, shape: 'flat' };
}

// ─────────────────────────────────────────────────────────────────────────────
//  Shared helpers for the shape-specific builders below
// ─────────────────────────────────────────────────────────────────────────────

/** Build a TableModelColumn from a field key + options (type inferred from a
 *  sample value when not given). Shared by every builder. */
function makeColumn(
  field: string,
  opts: Partial<TableModelColumn>,
  sampleValue: unknown,
  defaultWidth: number,
): TableModelColumn {
  const type = (opts.type as TableModelColumn['type'])
    || (toNum(sampleValue) !== null ? 'number' : 'text');
  return {
    key: field,
    label: opts.label || field,
    type,
    align: opts.align || (type === 'number' || type === 'currency' || type === 'percent' ? 'end' : 'start'),
    width: opts.width || defaultWidth,
    frozen: opts.frozen === true,
    control: opts.control,
    columnGroup: opts.columnGroup,
  };
}

/** Render a cell (display + conditional style) for a value in a column. */
function makeCell(
  value: unknown,
  col: TableModelColumn,
  capabilities: TableMatrixCapabilities,
  rules: TableMatrixConditionalRule[],
  applyRules: boolean,
): TableModelCell {
  const style = applyRules ? conditionalStyle(col.key, value, rules) : {};
  return { value, display: displayFor(value, col, capabilities), background: style.background, color: style.color };
}

/** Assemble a full row from a flat record over the given columns. */
function rowFrom(
  raw: Row,
  columns: TableModelColumn[],
  capabilities: TableMatrixCapabilities,
  rules: TableMatrixConditionalRule[],
  kind: TableModelRow['kind'],
  extra: Partial<TableModelRow> = {},
): TableModelRow {
  const cells: Record<string, TableModelCell> = {};
  columns.forEach((col) => { cells[col.key] = makeCell(raw[col.key], col, capabilities, rules, kind === 'data'); });
  return { cells, raw: kind === 'data' ? raw : undefined, kind, ...extra };
}

const defWidth = (c: TableMatrixCapabilities): number =>
  ((c.chartDefaults?.defaultColumnWidth as number) ?? FALLBACK.defaultColumnWidth);

// ─────────────────────────────────────────────────────────────────────────────
//  MATRIX — one row dimension down, one column dimension across (cross-tab)
// ─────────────────────────────────────────────────────────────────────────────
export function buildMatrixModel(safeRows: Row[], capabilities: TableMatrixCapabilities): TableModel {
  const rules = Array.isArray(capabilities.conditionalRules) ? capabilities.conditionalRules : [];
  const dw = defWidth(capabilities);
  const rowField = capabilities.rowField
    || capabilities.columns?.[0]?.field
    || (safeRows[0] ? Object.keys(safeRows[0])[0] : '');
  const colField = capabilities.columnField || '';
  const valField = capabilities.valueField || '';
  if (!rowField || !colField || !valField || !safeRows.length) {
    return { columns: [], rows: [], empty: true, shape: 'matrix' };
  }
  const colValues = distinct(safeRows, colField);
  const mode = aggMode(capabilities);

  // Columns: the row-dimension label column + one value column per column-value.
  const columns: TableModelColumn[] = [
    makeColumn(rowField, { label: rowField, frozen: capabilities.freezeFirstColumn !== false, type: 'text' }, safeRows[0][rowField], dw),
    ...colValues.map((cv) => makeColumn(`__m_${cv}`, { label: cv || '(blank)', type: 'number', columnGroup: colField }, safeRows.find((r) => String(r[colField] ?? '') === cv)?.[valField], dw)),
  ];
  const columnGroups: TableModelColumnGroup[] = [{ label: colField, keys: colValues.map((cv) => `__m_${cv}`) }];

  // One row per distinct row-dimension value; each cell = aggregated measure.
  const rowValues = distinct(safeRows, rowField);
  const modelRows: TableModelRow[] = rowValues.map((rv) => {
    const rowRows = safeRows.filter((r) => String(r[rowField] ?? '') === rv);
    const raw: Row = { [rowField]: rv };
    colValues.forEach((cv) => {
      const cellRows = rowRows.filter((r) => String(r[colField] ?? '') === cv);
      raw[`__m_${cv}`] = cellRows.length ? aggField(cellRows, valField, mode) : '';
    });
    return rowFrom(raw, columns, capabilities, rules, 'data');
  });

  // Grand total row across all row-values, per column-value.
  if (capabilities.showGrandTotal === true) {
    const raw: Row = { [rowField]: FALLBACK.grandTotalLabel };
    colValues.forEach((cv) => {
      const cellRows = safeRows.filter((r) => String(r[colField] ?? '') === cv);
      raw[`__m_${cv}`] = aggField(cellRows, valField, mode);
    });
    modelRows.push(rowFrom(raw, columns, capabilities, rules, 'grandtotal'));
  }

  return { columns, rows: modelRows, empty: !modelRows.length, columnGroups, shape: 'matrix' };
}

// ─────────────────────────────────────────────────────────────────────────────
//  PIVOT — N row fields × N column fields × a measure (generalised matrix)
// ─────────────────────────────────────────────────────────────────────────────
export function buildPivotModel(safeRows: Row[], capabilities: TableMatrixCapabilities): TableModel {
  const rules = Array.isArray(capabilities.conditionalRules) ? capabilities.conditionalRules : [];
  const dw = defWidth(capabilities);
  const rowFields = (capabilities.rowFields || []).filter(Boolean);
  const colFields = (capabilities.columnFields || []).filter(Boolean);
  const valField = capabilities.valueField || '';
  if (!rowFields.length || !colFields.length || !valField || !safeRows.length) {
    return { columns: [], rows: [], empty: true, shape: 'pivot' };
  }
  const mode = aggMode(capabilities);
  // Column keys = the joined distinct combination of the column fields.
  const colKeyOf = (r: Row) => colFields.map((f) => String(r[f] ?? '')).join(' / ');
  const colKeys: string[] = [];
  const seenCK = new Set<string>();
  safeRows.forEach((r) => { const k = colKeyOf(r); if (!seenCK.has(k)) { seenCK.add(k); colKeys.push(k); } });

  const columns: TableModelColumn[] = [
    ...rowFields.map((f, i) => makeColumn(f, { label: f, frozen: i === 0 && capabilities.freezeFirstColumn !== false, type: 'text' }, safeRows[0][f], dw)),
    ...colKeys.map((ck) => makeColumn(`__p_${ck}`, { label: ck || '(blank)', type: 'number', columnGroup: colFields.join(' / ') }, undefined, dw)),
  ];
  const columnGroups: TableModelColumnGroup[] = [{ label: colFields.join(' / '), keys: colKeys.map((ck) => `__p_${ck}`) }];

  const rowKeyOf = (r: Row) => rowFields.map((f) => String(r[f] ?? '')).join('');
  const rowGroups: Record<string, Row[]> = {};
  const rowOrder: string[] = [];
  safeRows.forEach((r) => { const k = rowKeyOf(r); (rowGroups[k] ||= (rowOrder.push(k), [])).push(r); });

  const modelRows: TableModelRow[] = rowOrder.map((rk) => {
    const grp = rowGroups[rk];
    const raw: Row = {};
    rowFields.forEach((f) => { raw[f] = grp[0][f]; });
    colKeys.forEach((ck) => {
      const cellRows = grp.filter((r) => colKeyOf(r) === ck);
      raw[`__p_${ck}`] = cellRows.length ? aggField(cellRows, valField, mode) : '';
    });
    return rowFrom(raw, columns, capabilities, rules, 'data');
  });

  if (capabilities.showGrandTotal === true) {
    const raw: Row = { [rowFields[0]]: FALLBACK.grandTotalLabel };
    colKeys.forEach((ck) => { raw[`__p_${ck}`] = aggField(safeRows.filter((r) => colKeyOf(r) === ck), valField, mode); });
    modelRows.push(rowFrom(raw, columns, capabilities, rules, 'grandtotal'));
  }

  return { columns, rows: modelRows, empty: !modelRows.length, columnGroups, shape: 'pivot' };
}

// ─────────────────────────────────────────────────────────────────────────────
//  GROUPED — multi-level row grouping with group-header rows + subtotals
// ─────────────────────────────────────────────────────────────────────────────
export function buildGroupedModel(safeRows: Row[], capabilities: TableMatrixCapabilities): TableModel {
  const rules = Array.isArray(capabilities.conditionalRules) ? capabilities.conditionalRules : [];
  const dw = defWidth(capabilities);
  const groupFields = (capabilities.groupByFields && capabilities.groupByFields.length
    ? capabilities.groupByFields
    : (capabilities.groupBy ? [capabilities.groupBy] : [])).filter(Boolean);
  const dataCols = Array.isArray(capabilities.columns) && capabilities.columns.length
    ? capabilities.columns.map((c) => c.field)
    : (safeRows[0] ? Object.keys(safeRows[0]) : []);
  if (!groupFields.length || !safeRows.length || !dataCols.length) {
    // No grouping configured → fall back to a flat model.
    return { ...buildTableModel(safeRows, { ...capabilities, typeFlags: {}, tableType: 'normal' }), shape: 'grouped' };
  }
  const columns: TableModelColumn[] = dataCols.map((f, i) =>
    makeColumn(f, { frozen: i === 0 && capabilities.freezeFirstColumn === true }, safeRows[0]?.[f], dw));
  const numericCols = columns.filter((c) => c.type === 'number' || c.type === 'currency' || c.type === 'percent');
  const mode = aggMode(capabilities);
  const wantSubtotals = capabilities.showSubtotals === true;
  const modelRows: TableModelRow[] = [];

  const walk = (rows: Row[], level: number, parentKey: string) => {
    const field = groupFields[level];
    distinct(rows, field).forEach((g) => {
      const groupRows = rows.filter((r) => String(r[field] ?? '') === g);
      const key = parentKey ? `${parentKey}${g}` : g;
      // Group-header row (label in the first column, indented by level).
      const header: Row = { [columns[0].key]: g };
      modelRows.push(rowFrom(header, columns, capabilities, rules, 'group', {
        depth: level, expandable: true, expanded: capabilities.groupsExpanded !== false, rowKey: key, parentKey: parentKey || undefined, groupLabel: g,
      }));
      if (level + 1 < groupFields.length) {
        walk(groupRows, level + 1, key);
      } else {
        groupRows.forEach((r) => modelRows.push(rowFrom(r, columns, capabilities, rules, 'data', { depth: level + 1, parentKey: key })));
      }
      if (wantSubtotals) {
        const sub: Row = { [columns[0].key]: `${g} ${FALLBACK.totalGroupLabel}` };
        numericCols.forEach((col) => { sub[col.key] = aggField(groupRows, col.key, mode); });
        modelRows.push(rowFrom(sub, columns, capabilities, rules, 'subtotal', { depth: level, parentKey: key, groupLabel: g }));
      }
    });
  };
  walk(safeRows, 0, '');

  if (capabilities.showGrandTotal === true && numericCols.length) {
    const grand: Row = { [columns[0].key]: FALLBACK.grandTotalLabel };
    numericCols.forEach((col) => { grand[col.key] = aggField(safeRows, col.key, mode); });
    modelRows.push(rowFrom(grand, columns, capabilities, rules, 'grandtotal'));
  }
  return { columns, rows: modelRows, empty: !modelRows.length, shape: 'grouped' };
}

// ─────────────────────────────────────────────────────────────────────────────
//  TREE GRID — self-referential parent/child hierarchy (client-side tree)
// ─────────────────────────────────────────────────────────────────────────────
export function buildTreeGridModel(safeRows: Row[], capabilities: TableMatrixCapabilities): TableModel {
  const rules = Array.isArray(capabilities.conditionalRules) ? capabilities.conditionalRules : [];
  const dw = defWidth(capabilities);
  const idField = capabilities.treeIdField || '';
  const parentField = capabilities.treeParentField || '';
  const dataCols = Array.isArray(capabilities.columns) && capabilities.columns.length
    ? capabilities.columns.map((c) => c.field)
    : (safeRows[0] ? Object.keys(safeRows[0]) : []);
  if (!idField || !parentField || !safeRows.length || !dataCols.length) {
    return { columns: [], rows: [], empty: true, shape: 'treegrid' };
  }
  const columns: TableModelColumn[] = dataCols.map((f, i) =>
    makeColumn(f, { frozen: i === 0 && capabilities.freezeFirstColumn === true }, safeRows[0]?.[f], dw));

  const childrenOf: Record<string, Row[]> = {};
  const ids = new Set(safeRows.map((r) => String(r[idField] ?? '')));
  safeRows.forEach((r) => {
    const p = String(r[parentField] ?? '');
    const parent = ids.has(p) ? p : '__root';
    (childrenOf[parent] ||= []).push(r);
  });
  const modelRows: TableModelRow[] = [];
  const emit = (parentId: string, depth: number, parentKey: string) => {
    (childrenOf[parentId] || []).forEach((r) => {
      const id = String(r[idField] ?? '');
      const kids = childrenOf[id] || [];
      const key = parentKey ? `${parentKey}${id}` : id;
      modelRows.push(rowFrom(r, columns, capabilities, rules, 'data', {
        depth, expandable: kids.length > 0, expanded: capabilities.groupsExpanded !== false, rowKey: key, parentKey: parentKey || undefined,
      }));
      if (kids.length) emit(id, depth + 1, key);
    });
  };
  emit('__root', 0, '');
  return { columns, rows: modelRows, empty: !modelRows.length, shape: 'treegrid' };
}

// ─────────────────────────────────────────────────────────────────────────────
//  FINANCIAL STATEMENT — indented line items with role-based formatting
// ─────────────────────────────────────────────────────────────────────────────
export function buildFinancialModel(safeRows: Row[], capabilities: TableMatrixCapabilities): TableModel {
  const rules = Array.isArray(capabilities.conditionalRules) ? capabilities.conditionalRules : [];
  const dw = defWidth(capabilities);
  const roleField = capabilities.roleField || '';
  const indentField = capabilities.indentField || '';
  const dataCols = (Array.isArray(capabilities.columns) && capabilities.columns.length
    ? capabilities.columns.map((c) => c.field)
    : (safeRows[0] ? Object.keys(safeRows[0]) : []))
    .filter((f) => f !== roleField && f !== indentField);
  if (!safeRows.length || !dataCols.length) {
    return { columns: [], rows: [], empty: true, shape: 'financial' };
  }
  const columns: TableModelColumn[] = dataCols.map((f, i) =>
    makeColumn(f, { frozen: i === 0 && capabilities.freezeFirstColumn === true }, safeRows[0]?.[f], dw));

  const modelRows: TableModelRow[] = safeRows.map((r) => {
    const role = String((roleField && r[roleField]) || 'item').toLowerCase();
    const kind: TableModelRow['kind'] = role === 'header' ? 'header'
      : role === 'subtotal' ? 'subtotal'
      : role === 'total' ? 'total'
      : 'data';
    const depth = indentField ? Math.max(0, Math.floor(toNum(r[indentField]) ?? 0)) : 0;
    return rowFrom(r, columns, capabilities, rules, kind, { role, depth });
  });
  return { columns, rows: modelRows, empty: !modelRows.length, shape: 'financial' };
}
